/**
 * Storage abstraction. Components MUST NOT touch localStorage directly.
 *
 * Currently this delegates to localStorage. When the app is wrapped with
 * Capacitor, this single file is the swap point — replace the body with
 * calls to @capacitor/preferences (async API, same interface).
 */

const memoryFallback = new Map();
const hasLocalStorage = (() => {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    return false;
  }
})();

export async function getItem(key) {
  // memoryFallback wins when present: it holds the freshest value for keys
  // whose last `setItem` couldn't reach localStorage (quota exceeded, private
  // mode, etc.). Reading from localStorage would return the pre-failure
  // snapshot and silently roll back the child's progress.
  if (memoryFallback.has(key)) {
    return safeParse(memoryFallback.get(key));
  }
  if (hasLocalStorage) {
    const raw = window.localStorage.getItem(key);
    return raw === null ? null : safeParse(raw);
  }
  return null;
}

export async function setItem(key, value) {
  const serialized = JSON.stringify(value);
  if (hasLocalStorage) {
    try {
      window.localStorage.setItem(key, serialized);
      // Clear any stale memory fallback now that localStorage holds the
      // authoritative value again.
      memoryFallback.delete(key);
      return;
    } catch (e) {
      // Quota exceeded or storage unavailable — fall back silently
      console.warn('Storage write failed, falling back to memory:', e);
    }
  }
  memoryFallback.set(key, serialized);
}

export async function removeItem(key) {
  if (hasLocalStorage) {
    window.localStorage.removeItem(key);
  }
  memoryFallback.delete(key);
}

export async function clearAll() {
  if (hasLocalStorage) {
    window.localStorage.clear();
  }
  memoryFallback.clear();
}

function safeParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export default { getItem, setItem, removeItem, clearAll };
